import { BuildingIcon, Trash2Icon } from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'

import { errorMessage } from '@/api/client'
import {
  useAddTaxId,
  useBillingDetails,
  useDeleteTaxId,
  useTaxIds,
  useUpdateBillingDetails,
} from '@/api/queries'
import type { BillingAddress } from '@/api/types'
import { ErrorState, TableSkeleton } from '@/components/states'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SELECT_CLASS as staffSelectClass } from '@/staff/shared'
import { taxIdCountries, taxIdLabel, taxIdProblem, taxIdTypesFor } from '@/lib/tax-ids'

const EMPTY: BillingAddress = { line1: '', line2: '', city: '', state: '', postal_code: '', country: '' }

const FIELDS: { key: keyof BillingAddress; label: string; autoComplete: string; hint?: string }[] = [
  { key: 'line1', label: 'Address line 1', autoComplete: 'address-line1' },
  { key: 'line2', label: 'Address line 2', autoComplete: 'address-line2' },
  { key: 'city', label: 'City', autoComplete: 'address-level2' },
  { key: 'state', label: 'State / province', autoComplete: 'address-level1' },
  { key: 'postal_code', label: 'Postal code', autoComplete: 'postal-code' },
  { key: 'country', label: 'Country (2 letters)', autoComplete: 'country', hint: 'ISO code, for example US or ES.' },
]

function TaxIds({ readOnly }: { readOnly: boolean }) {
  const ids = useTaxIds()
  const add = useAddTaxId()
  const remove = useDeleteTaxId()
  const [country, setCountry] = useState('')
  const [type, setType] = useState('')
  const [value, setValue] = useState('')
  const problem = taxIdProblem(type, value)
  const types = taxIdTypesFor(country)

  function submit(event: FormEvent) {
    event.preventDefault()
    if (problem !== null) return
    add.mutate(
      { type, value: value.trim() },
      {
        onSuccess: () => {
          setValue('')
        },
      },
    )
  }

  return (
    <section className="flex flex-col gap-3" aria-label="Tax IDs" data-testid="tax-ids">
      <h3 className="text-sm font-medium">Tax IDs</h3>
      {ids.isPending ? (
        <TableSkeleton rows={1} columns={2} />
      ) : ids.isError ? (
        <ErrorState error={ids.error} title="Could not load tax IDs" onRetry={() => void ids.refetch()} />
      ) : ids.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">No tax ID on file. Add your VAT or business number to have it on invoices.</p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border">
          {ids.data.map((id) => (
            <li key={id.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm" data-testid="tax-id-row">
              <span className="font-medium">{taxIdLabel(id.type)}</span>
              <span className="font-mono text-xs">{id.value}</span>
              {id.verification === null ? null : (
                <span className="text-xs text-muted-foreground">{id.verification}</span>
              )}
              {readOnly ? null : (
                <Button
                  size="icon-sm"
                  variant="ghost"
                  className="ml-auto text-destructive"
                  aria-label={`Remove ${id.value}`}
                  disabled={remove.isPending}
                  onClick={() => {
                    remove.mutate(id.id)
                  }}
                >
                  <Trash2Icon className="size-3.5" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {remove.isError ? (
        <Alert variant="destructive">
          <AlertDescription>{errorMessage(remove.error)}</AlertDescription>
        </Alert>
      ) : null}
      {readOnly ? null : (
        <form className="grid gap-3 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end" onSubmit={submit}>
          <div className="space-y-1.5">
            <Label htmlFor="tax-country">Country</Label>
            <select
              id="tax-country"
              className={staffSelectClass}
              value={country}
              onChange={(event) => {
                setCountry(event.target.value)
                setType(taxIdTypesFor(event.target.value)[0]?.code ?? '')
              }}
            >
              <option value="">Choose…</option>
              {taxIdCountries().map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tax-type">Type</Label>
            <select
              id="tax-type"
              className={staffSelectClass}
              value={type}
              disabled={types.length === 0}
              onChange={(event) => {
                setType(event.target.value)
              }}
            >
              {types.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tax-value">Number</Label>
            <Input
              id="tax-value"
              value={value}
              onChange={(event) => {
                setValue(event.target.value)
              }}
              className="font-mono"
              autoComplete="off"
            />
          </div>
          <Button type="submit" variant="outline" disabled={problem !== null || add.isPending} data-testid="add-tax-id">
            {add.isPending ? 'Adding…' : 'Add tax ID'}
          </Button>
        </form>
      )}
      {add.isError ? (
        <Alert variant="destructive" data-testid="tax-id-error">
          <AlertDescription>{errorMessage(add.error)}</AlertDescription>
        </Alert>
      ) : null}
    </section>
  )
}

/** Name, billing address and tax IDs. An address change takes effect on the next invoice's tax. */
export function BillingDetailsCard({ readOnly }: { readOnly: boolean }) {
  const details = useBillingDetails()
  const save = useUpdateBillingDetails()
  const [name, setName] = useState('')
  const [address, setAddress] = useState<BillingAddress>(EMPTY)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (details.data === undefined) return
    setName(details.data.name ?? '')
    setAddress({ ...EMPTY, ...Object.fromEntries(Object.entries(details.data.address).map(([k, v]) => [k, v ?? ''])) })
  }, [details.data])

  function submit(event: FormEvent) {
    event.preventDefault()
    setSaved(false)
    save.mutate(
      { name: name.trim(), address },
      {
        onSuccess: () => {
          setSaved(true)
        },
      },
    )
  }

  return (
    <Card data-testid="billing-details">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BuildingIcon className="size-4 text-primary" />
          Billing details
        </CardTitle>
        <CardDescription>
          Printed on your invoices and used to calculate tax. Changes apply from your next invoice. Invoice emails go
          to every administrator and member with billing access.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {details.isPending ? (
          <TableSkeleton rows={3} columns={2} />
        ) : details.isError ? (
          <ErrorState error={details.error} title="Could not load billing details" onRetry={() => void details.refetch()} />
        ) : (
          <form className="grid gap-4 sm:grid-cols-2" onSubmit={submit}>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="bd-name">Name or company</Label>
              <Input
                id="bd-name"
                value={name}
                disabled={readOnly}
                autoComplete="organization"
                onChange={(event) => {
                  setName(event.target.value)
                }}
              />
            </div>
            {FIELDS.map((field) => (
              <div key={field.key} className="space-y-1.5">
                <Label htmlFor={`bd-${field.key}`}>{field.label}</Label>
                <Input
                  id={`bd-${field.key}`}
                  value={address[field.key] ?? ''}
                  disabled={readOnly}
                  autoComplete={field.autoComplete}
                  maxLength={field.key === 'country' ? 2 : undefined}
                  onChange={(event) => {
                    setAddress((current) => ({
                      ...current,
                      [field.key]: field.key === 'country' ? event.target.value.toUpperCase() : event.target.value,
                    }))
                  }}
                />
              </div>
            ))}
            {save.isError ? (
              <Alert variant="destructive" className="sm:col-span-2" data-testid="details-error">
                <AlertDescription>{errorMessage(save.error)}</AlertDescription>
              </Alert>
            ) : null}
            {saved ? (
              <Alert variant="info" className="sm:col-span-2" data-testid="details-saved">
                <AlertDescription>Saved. The new address applies from your next invoice.</AlertDescription>
              </Alert>
            ) : null}
            {readOnly ? null : (
              <div className="sm:col-span-2">
                <Button type="submit" disabled={save.isPending} data-testid="save-details">
                  {save.isPending ? 'Saving…' : 'Save details'}
                </Button>
              </div>
            )}
          </form>
        )}
        <TaxIds readOnly={readOnly} />
      </CardContent>
    </Card>
  )
}
